import { Link, usePage } from "@inertiajs/react";
import AnalyticsCookieSettings from "@/components/marketing/AnalyticsCookieSettings";

/**
 * Shared footer for marketing pages.
 * Wordmark + columned navigation: product, read-a-thon guides, help & legal.
 * The "Read-a-thon guides" column links the content cluster pages from
 * every marketing page so they aren't reachable only via the sitemap.
 */
export default function MarketingFooter() {
  const { routes } = usePage().props;

  return (
    <footer className="border-t border-border/60 py-12 bg-card/30">
      <div className="container">
        <div className="grid grid-cols-2 gap-8 md:gap-10 md:grid-cols-[1.5fr_1fr_1.2fr_1fr]">
          <div className="col-span-2 md:col-span-1 space-y-2">
            <p className="font-display text-foreground">
              PagePledge<span className="text-primary">.</span>
            </p>
            <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
              Read-a-thon fundraising for Canadian schools. A flat fee per
              campaign, never a percentage of donations.
            </p>
          </div>

          <FooterColumn title="Product">
            <FooterLink href={routes.readathon_software}>Read-a-thon software</FooterLink>
            <FooterLink href={routes.pricing}>Pricing</FooterLink>
            <FooterLink href={routes.how_it_works}>How it works</FooterLink>
            <FooterLink href={routes.demo} newTab>Try the demo</FooterLink>
            <FooterLink href={routes.about_us}>About us</FooterLink>
            <FooterLink href={routes.new_school_application}>Sign up your school</FooterLink>
          </FooterColumn>

          <FooterColumn title="Read-a-thon guides" className="col-span-2 order-3 md:col-span-1 md:order-none">
            <FooterLink href="/read-a-thon">
              How to plan a readathon
            </FooterLink>
            <FooterLink href="/readathon-software-canada">
              Software for Canadian schools
            </FooterLink>
            <FooterLink href="/readathon-software-for-elementary-schools">
              Software for elementary schools
            </FooterLink>
            <FooterLink href="/readathon-app-for-schools">
              Read-a-thon app for schools
            </FooterLink>
            <FooterLink href="/readathon-donation-pages">
              Read-a-thon donation pages
            </FooterLink>
            <FooterLink href="/readathon-fundraising-ideas">
              Read-a-thon fundraising ideas
            </FooterLink>
            <FooterLink href="/readathon-tracking-spreadsheet-alternative">
              Free tracking template
            </FooterLink>
          </FooterColumn>

          <FooterColumn title="Help & legal">
            <FooterLink href={routes.help}>Help centre</FooterLink>
            <FooterLink href={routes.contact}>Contact</FooterLink>
            <FooterLink href={routes.privacy}>Privacy</FooterLink>
            <FooterLink href={routes.terms}>Terms</FooterLink>
            <AnalyticsCookieSettings className={FOOTER_LINK_CLASS} />
          </FooterColumn>
        </div>

        <p className="mt-10 pt-6 border-t border-border/60 text-sm text-muted-foreground font-display">
          Read-a-thons, made simple. Built in Canada.
        </p>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children, className = "" }) {
  return (
    <nav className={`space-y-3 ${className}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-foreground/70">
        {title}
      </p>
      <ul className="space-y-2">{children}</ul>
    </nav>
  );
}

const FOOTER_LINK_CLASS = "text-sm text-muted-foreground hover:text-primary transition-colors";

function FooterLink({ href, newTab = false, children }) {
  return (
    <li>
      {newTab ? (
        <a href={href} target="_blank" rel="noopener" className={FOOTER_LINK_CLASS}>{children}</a>
      ) : (
        <Link href={href} className={FOOTER_LINK_CLASS}>
          {children}
        </Link>
      )}
    </li>
  );
}
